import { useCallback, useMemo } from "react";
import { HeldEmailListItem } from "../../services/admin/systemEmails.service";
import {
  HeldEmailSelectionModel,
  updateHeldEmailSelection,
} from "../../components/admin/system-emails/heldEmailSelection";

const ACTIONABLE_STATES = new Set(["pending", "blocked", "failed"]);

export function useHeldEmailActionable(
  email: HeldEmailListItem,
  canRelease: boolean
) {
  return useMemo(
    () => canRelease && ACTIONABLE_STATES.has(email.state),
    [canRelease, email.state]
  );
}

export function useHeldEmailCheckbox(
  selection: HeldEmailSelectionModel,
  id: string
) {
  return useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      updateHeldEmailSelection(selection, id, event.currentTarget.checked),
    [id, selection]
  );
}

function hasSelectedAll(selection: HeldEmailSelectionModel): boolean {
  return (
    selection.actionableIds.length > 0 &&
    selection.actionableIds.every((id) => selection.selectedIds.has(id))
  );
}

function hasSelectedSome(selection: HeldEmailSelectionModel): boolean {
  return selection.actionableIds.some((id) => selection.selectedIds.has(id));
}

export function useHeldEmailSelectAll(selection: HeldEmailSelectionModel) {
  const all = useMemo(() => hasSelectedAll(selection), [selection]);
  const some = useMemo(
    () => !all && hasSelectedSome(selection),
    [all, selection]
  );
  const change = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      selection.onChange(
        event.currentTarget.checked
          ? new Set(selection.actionableIds)
          : new Set()
      ),
    [selection]
  );
  return { all, some, change };
}
